// Floating debug panel: thickness, intensity, bloom, vibration, bunching.

export interface DebugPanelHandlers {
  onThickness: (mul: number) => void;
  onIntensity: (mul: number) => void;
  onBloom: (scale: number) => void;
  onBloomThreshold: (t: number) => void;
  onVibrationFreq: (hz: number) => void;
  onVibrationGain: (gain: number) => void;
  onBunching: (thresholdNm: number) => void;
  onRippleSpeed: (pxPerSec: number) => void;
  onRippleSpacing: (sec: number) => void;
  onRippleThickness: (px: number) => void;
  onRippleBrightness: (mul: number) => void;
  onRippleXOffset: (px: number) => void;
  onHighlightIntensity: (mul: number) => void;
  onEnvFloor: (v: number) => void;
  onEnvExponent: (v: number) => void;
  onSpectrumBrightness: (v: number) => void;
  onRipples: (enabled: boolean) => void;
  onSound: (enabled: boolean) => void;
  initial: {
    thickness: number;
    intensity: number;
    bloom: number;
    bloomThreshold: number;
    vibrationFreq: number;
    vibrationGain: number;
    bunching: number;
    rippleSpeed: number;
    rippleSpacing: number;
    rippleThickness: number;
    rippleBrightness: number;
    rippleXOffset: number;
    highlightIntensity: number;
    envFloor: number;
    envExponent: number;
    spectrumBrightness: number;
    ripples: boolean;
    sound: boolean;
  };
}

export function mountDebugPanel(h: DebugPanelHandlers) {
  document.getElementById("debug-panel")?.remove();

  const root = document.createElement("div");
  root.id = "debug-panel";
  root.style.cssText = `
    position: fixed; top: 50px; right: 12px;
    z-index: 9001; width: 210px;
    max-height: calc(100vh - 70px);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: rgba(155,126,200,0.35) transparent;
    padding: 10px 12px;
    background: rgba(4,7,15,0.78);
    backdrop-filter: blur(14px);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px;
    font: 500 11px/1.5 'JetBrains Mono', monospace;
    color: #c8d0e0;
    pointer-events: auto;
    touch-action: auto;
  `;

  function row(
    label: string,
    min: number, max: number, step: number, value: number,
    fmt: (v: number) => string,
    onInput: (v: number) => void,
    debounceMs = 0,
  ) {
    const wrap = document.createElement("div");
    wrap.style.cssText = "display: flex; flex-direction: column; gap: 2px; margin: 6px 0;";
    const head = document.createElement("div");
    head.style.cssText = "display: flex; justify-content: space-between;";
    const labelEl = document.createElement("span");
    labelEl.textContent = label;
    const valEl = document.createElement("span");
    valEl.style.color = "#9b7ec8";
    valEl.textContent = fmt(value);
    head.appendChild(labelEl);
    head.appendChild(valEl);
    const slider = document.createElement("input");
    slider.type = "range";
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(step);
    slider.value = String(value);
    slider.style.cssText = "width: 100%; accent-color: #9b7ec8;";
    let timer: number | null = null;
    slider.addEventListener("input", () => {
      const v = parseFloat(slider.value);
      valEl.textContent = fmt(v);
      if (debounceMs > 0) {
        if (timer !== null) window.clearTimeout(timer);
        timer = window.setTimeout(() => { timer = null; onInput(v); }, debounceMs);
      } else {
        onInput(v);
      }
    });
    slider.addEventListener("pointerdown", (e) => e.stopPropagation());
    wrap.appendChild(head);
    wrap.appendChild(slider);
    root.appendChild(wrap);
  }

  function toggle(
    label: string,
    value: boolean,
    onChange: (v: boolean) => void,
  ) {
    const wrap = document.createElement("div");
    wrap.style.cssText = "display: flex; justify-content: space-between; align-items: center; margin: 6px 0;";
    const labelEl = document.createElement("span");
    labelEl.textContent = label;

    const btn = document.createElement("button");
    let on = value;
    const refresh = () => {
      btn.textContent = on ? "ON" : "OFF";
      btn.style.cssText = `
        padding: 1px 10px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.15);
        font: 500 10px 'JetBrains Mono', monospace; cursor: pointer; letter-spacing: 0.05em;
        background: ${on ? "rgba(100,200,120,0.18)" : "rgba(200,60,60,0.18)"};
        color: ${on ? "#80e8a0" : "#e88080"};
      `;
    };
    refresh();
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      on = !on;
      refresh();
      onChange(on);
    });
    btn.addEventListener("pointerdown", (e) => e.stopPropagation());

    wrap.appendChild(labelEl);
    wrap.appendChild(btn);
    root.appendChild(wrap);
  }

  function divider(label: string) {
    const el = document.createElement("div");
    el.style.cssText = "margin: 10px 0 4px; font-size: 9px; letter-spacing: 0.1em; opacity: 0.45; border-top: 1px solid rgba(255,255,255,0.07); padding-top: 6px;";
    el.textContent = label.toUpperCase();
    root.appendChild(el);
  }

  const title = document.createElement("div");
  title.textContent = "DEBUG";
  title.style.cssText = "letter-spacing: 0.12em; opacity: 0.6; font-size: 10px; margin-bottom: 4px;";
  root.appendChild(title);

  toggle("sound", h.initial.sound, h.onSound);

  divider("lines");
  row("thickness",     0.25, 8,    0.05, h.initial.thickness,       (v) => `${v.toFixed(2)}×`,    h.onThickness);
  row("intensity",     0.1,  6,    0.05, h.initial.intensity,       (v) => `${v.toFixed(2)}×`,    h.onIntensity);
  row("highlight",     1,    100,   0.2,  h.initial.highlightIntensity,(v)=>`${v.toFixed(1)}×`,    h.onHighlightIntensity);
  row("env floor",     0,    1,    0.01, h.initial.envFloor,        (v) => `${v.toFixed(2)}`,     h.onEnvFloor);
  row("env exponent",  0.1,  4,    0.05, h.initial.envExponent,     (v) => `${v.toFixed(2)}`,     h.onEnvExponent);

  divider("bloom");
  row("bloom scale",   0,    3,    0.05, h.initial.bloom,           (v) => `${v.toFixed(2)}`,     h.onBloom);
  row("bloom thresh",  0,    1,    0.02, h.initial.bloomThreshold,  (v) => `${v.toFixed(2)}`,     h.onBloomThreshold);

  divider("vibration");
  row("vib freq Hz",   1,    40,   0.5,  h.initial.vibrationFreq,   (v) => `${v.toFixed(1)} Hz`,  h.onVibrationFreq);
  row("vib gain",      0,    300,  1,    h.initial.vibrationGain,   (v) => `${v.toFixed(0)}×`,    h.onVibrationGain);

  divider("ripples");
  toggle("ripples", h.initial.ripples, h.onRipples);
  row("ripple speed",  100,  2000, 25,   h.initial.rippleSpeed,     (v) => `${v.toFixed(0)} px/s`, h.onRippleSpeed);
  row("ripple spacing", 0.1, 2.0,  0.05, h.initial.rippleSpacing,   (v) => `${v.toFixed(2)} s`,   h.onRippleSpacing);
  row("ripple thickness", 4, 120,  2,    h.initial.rippleThickness, (v) => `${v.toFixed(0)} px`,  h.onRippleThickness);
  row("ripple bright", 0,    3,    0.05, h.initial.rippleBrightness,(v) => `${v.toFixed(2)}×`,    h.onRippleBrightness);
  row("ripple Δx",     -200, 200,  1,    h.initial.rippleXOffset,   (v) => `${v.toFixed(0)} px`,  h.onRippleXOffset);

  divider("background");
  row("spectrum",      0,    1,    0.01, h.initial.spectrumBrightness,(v)=>`${v.toFixed(2)}`,     h.onSpectrumBrightness);

  divider("element");
  row("bunch nm",      0,    5,    0.05, h.initial.bunching,        (v) => `${v.toFixed(2)} nm`,  h.onBunching, 200);

  root.addEventListener("pointerdown", (e) => e.stopPropagation());
  document.body.appendChild(root);
}
